import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { DateTime } from "luxon";

import { formatMoney } from "../../DashboardView/dashboardHelpers";
import { packWindow, dayLabel } from "../lessonHelpers";

// A paid lesson moves with the money or package session that paid for it — so only
// times that source can pay for are listed.
export const reschedulePaymentCopy = (info, currency) =>
  info.mode === "MONEY_MOVE"
    ? `The ${formatMoney(info.creditAmount, currency)} you paid moves with you, so only times at that price are shown.`
    : `Your ${info.pack?.name ? `"${info.pack.name}"` : "package"} session moves with you.`;

// One line for what paid for the lesson, and how far that reaches.
const paidWithLine = (info, currency) => {
  if (info.mode === "MONEY_MOVE") return `Paid ${formatMoney(info.creditAmount, currency)} · only times at this price`;
  const pack = packWindow(info);
  const name = info.pack?.name || "your package";
  if (pack?.end) return `Paid with ${name} · valid to ${dayLabel(pack.end)}`;
  if (pack?.start) return `Paid with ${name} · valid from ${dayLabel(pack.start)}`;
  return `Paid with ${name}`;
};

// The lesson being moved, and what pays for it, in two lines with "Keep it" beside them.
// The page title already says "Move your lesson"; this names which one, and stays short
// so the times keep the screen — on a phone the filters and the date strip hold the top.
function LessonRescheduleBanner({ reschedule, currency, sx }) {
  const info = reschedule.info;
  const from = info?.from;
  const when = from ? [DateTime.fromISO(from.date).toFormat("ccc d LLL"), from.time, `${from.duration} min`].join(" · ") : null;
  const who = from ? [from.trainerName, from.locationName].filter(Boolean).join(" · ") : null;

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
        display: "flex",
        alignItems: "center",
        gap: 1,
        ...sx
      }}
    >
      <EventRepeatIcon sx={{ color: "primary.main", fontSize: "1.1rem", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.3 }}>
          {when || "Loading…"}
          {who && (
            <Box component="span" sx={{ fontWeight: 500, color: "text.secondary" }}>
              {` · ${who}`}
            </Box>
          )}
        </Typography>
        {info && (
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {paidWithLine(info, currency)}
          </Typography>
        )}
      </Box>
      <Button size="small" onClick={reschedule.cancel} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>
        Keep it
      </Button>
    </Paper>
  );
}

export default LessonRescheduleBanner;
